import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { AutoAwesome, CloudUpload, Delete, Save } from '@mui/icons-material';
import { deviceApi, fileStoreApi } from '../api';
import { DeviceFileInfo } from '../types';
import {
  ConfigSchema,
  ConfigValues,
  deviceItemTypes,
  generateConfigJson,
  inspectConfigFile,
  mergeConfig,
  deviceSupportedConfigVersion,
  schemaGroupsForLayer,
  settingsNewerThanFirmware,
} from '../schema/schema';
import ConfigMigrateBanner from '../components/ConfigMigrateBanner';
import SchemaForm from '../components/SchemaForm';
import DeviceInspector from './DeviceInspector';
import PeripheryDialog from './PeripheryDialog';
import { catalogWizardFor } from '../flow/catalogWizard';
import {
  GraphDevice,
  GraphRoute,
  MAX_DEVICES,
  SettingsBag,
  ensureGraph,
  mergeRoutes,
  readDevices,
  readRoutes,
} from '../flow/graph';
import { useI18n } from '../i18n';
import { CONTROLLERS_PATH, setupSignalsPath } from '../nav/paths';

const PATH = '/settings.json';

const DeviceConfigEditor: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { deviceId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = deviceId;
  const selectedInstance = searchParams.get('instance') || '';

  const [controller, setController] = useState<DeviceFileInfo | null>(null);
  const [schema, setSchema] = useState<ConfigSchema | null>(null);
  const [content, setContent] = useState('');
  const [savedContent, setSavedContent] = useState('');
  const [values, setValues] = useState<ConfigValues>({});
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const dirty = content !== savedContent;
  const configInfo = schema ? inspectConfigFile(content, schema) : null;
  const pushBlocked = Boolean(
    schema &&
    settingsNewerThanFirmware(configInfo?.version ?? null, deviceSupportedConfigVersion(controller)),
  );

  const itemTypes = useMemo(() => (schema ? deviceItemTypes(schema) : {}), [schema]);
  const connectionGroupIds = schema ? schemaGroupsForLayer(schema, 'connection').map((group) => group.id) : [];

  const devices = useMemo(() => {
    const graph = ensureGraph(values as Record<string, unknown>);
    return readDevices(graph);
  }, [values]);

  const listed = useMemo(() => devices.filter((device) => {
    const spec = itemTypes[String(device.type)];
    return spec ? spec.layer !== 'connection' : false;
  }), [devices, itemTypes]);

  const activeDevice = listed.find((d) => d.id === selectedInstance) || null;

  const loadController = useCallback(async () => {
    if (!selectedId) {
      setController(null);
      return;
    }
    const store = await fileStoreApi.getStore();
    setController((store.devices || []).find((item) => item.id === selectedId) || null);
  }, [selectedId]);

  const applySettings = useCallback((raw: string, nextSchema: ConfigSchema | null) => {
    setContent(raw);
    if (!nextSchema) {
      return;
    }
    const merged = mergeConfig(nextSchema, raw);
    setValues(merged);
  }, []);

  const writeDevices = useCallback((nextDevices: GraphDevice[], nextRoutes?: GraphRoute[]) => {
    if (!schema) {
      return;
    }
    const payload: Record<string, unknown> = { ...values, devices: nextDevices };
    if (nextRoutes) {
      payload.routes = mergeRoutes(readRoutes(values as SettingsBag), nextRoutes);
    }
    const next = ensureGraph(payload);
    setValues(next as ConfigValues);
    setContent(generateConfigJson(schema, next as ConfigValues, content));
  }, [schema, values, content]);

  const setInstance = (instance: string) => {
    const next = new URLSearchParams(searchParams);
    if (instance) {
      next.set('instance', instance);
    } else {
      next.delete('instance');
    }
    next.delete('id');
    setSearchParams(next);
  };

  useEffect(() => {
    loadController().catch((err) => setError(err?.message || t('deviceConfig.loadFailed')));
    fileStoreApi.getSchema().then(setSchema).catch((err) => setError(err?.message || t('files.schemaFailed')));
  }, [loadController, t]);

  useEffect(() => {
    if (!selectedId || !schema) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const stored = await deviceApi.getStoredFile(selectedId, PATH);
        if (cancelled) {
          return;
        }
        const raw = stored?.content || '';
        setSavedContent(raw);
        applySettings(raw, schema);
        setError(null);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || t('deviceConfig.loadFailed'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId, schema, applySettings, t]);

  useEffect(() => {
    if (!selectedInstance && listed.length > 0) {
      setInstance(listed[0].id);
    }
    // Only pick a default when the list first has rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listed, selectedInstance]);

  const handleSave = async () => {
    if (!selectedId) {
      return;
    }
    try {
      setBusy(true);
      await deviceApi.saveStoredFile(selectedId, PATH, content);
      setSavedContent(content);
      setStatus(t('deviceConfig.saved'));
      setError(null);
      await loadController();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || t('deviceConfig.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handlePush = async () => {
    if (!selectedId || pushBlocked) {
      return;
    }
    if (!window.confirm(t('files.settingsPushConfirm'))) {
      return;
    }
    try {
      setBusy(true);
      await deviceApi.saveStoredFile(selectedId, PATH, content);
      setSavedContent(content);
      await deviceApi.pushStoredFile(selectedId, PATH);
      setStatus(t('deviceConfig.pushed'));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || t('deviceConfig.pushFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveDevice = () => {
    if (!activeDevice || !window.confirm(t('deviceConfig.removeConfirm', { id: activeDevice.id }))) {
      return;
    }
    const next = devices.filter((d) => d.id !== activeDevice.id);
    writeDevices(next);
    setInstance(next.filter((d) => itemTypes[String(d.type)]?.layer !== 'connection')[0]?.id || '');
  };

  if (!selectedId) {
    return <Navigate to={CONTROLLERS_PATH} replace />;
  }

  return (
    <Container maxWidth={false} sx={{ py: 2, px: { xs: 2, md: 3 }, overflow: 'auto', height: '100%' }}>
      <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" mb={2}>
        <Button
          variant="contained"
          startIcon={<AutoAwesome />}
          disabled={busy || listed.length >= MAX_DEVICES}
          onClick={() => setAddOpen(true)}
        >
          {t('deviceConfig.wizard.title')}
        </Button>
        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={!selectedId || busy || !dirty}>{t('common.save')}</Button>
        <Button variant="outlined" startIcon={<CloudUpload />} onClick={handlePush} disabled={!selectedId || busy || dirty || pushBlocked}>{t('deviceConfig.push')}</Button>
        {dirty && <Typography variant="caption" color="warning.main">{t('common.unsaved')}</Typography>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {status && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setStatus(null)}>{status}</Alert>}
      {schema && (
        <ConfigMigrateBanner
          schema={schema}
          raw={content}
          disabled={busy}
          onMigrated={(next) => { setContent(next); applySettings(next, schema); }}
        />
      )}

      <Typography color="text.secondary" sx={{ mb: 2 }}>{t('deviceConfig.intro')}</Typography>

      <Box display="flex" gap={2} alignItems="stretch" sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
        <Paper sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, p: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ px: 1, pt: 1 }}>
            <Typography variant="subtitle2">{t('deviceConfig.instances')}</Typography>
            <Button
              size="small"
              startIcon={<AutoAwesome />}
              disabled={busy || listed.length >= MAX_DEVICES}
              onClick={() => setAddOpen(true)}
            >
              {t('deviceConfig.wizard.title')}
            </Button>
          </Box>
          <List dense>
            {listed.map((device) => (
              <ListItemButton
                key={device.id}
                selected={selectedInstance === device.id}
                onClick={() => setInstance(device.id)}
              >
                <ListItemText
                  primary={device.id}
                  secondary={t(`flow.types.${device.type}`)}
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {schema && connectionGroupIds.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('deviceConfig.connections')}</Typography>
              <SchemaForm
                schema={schema}
                values={values}
                disabled={busy}
                groupIds={connectionGroupIds}
                onChange={(next) => {
                  const graph = ensureGraph(next as Record<string, unknown>);
                  setValues(graph as ConfigValues);
                  setContent(generateConfigJson(schema, graph as ConfigValues, content));
                }}
              />
            </Paper>
          )}
          <Paper sx={{ p: 2 }}>
            {!activeDevice ? (
              <Typography color="text.secondary">{t('deviceConfig.selectInstance')}</Typography>
            ) : (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">{activeDevice.id}</Typography>
                  <Button color="error" startIcon={<Delete />} onClick={handleRemoveDevice} disabled={busy}>{t('common.delete')}</Button>
                </Box>
                {schema && (
                  <DeviceInspector
                    device={activeDevice}
                    schema={schema}
                    existingDevices={devices}
                    disabled={busy}
                    onEditSignals={() => navigate(setupSignalsPath(selectedId))}
                    onChange={(next) => {
                      const updated = devices.map((d) => (d.id === activeDevice.id ? next : d));
                      writeDevices(updated);
                      if (next.id !== activeDevice.id) {
                        setInstance(next.id);
                      }
                    }}
                  />
                )}
              </>
            )}
          </Paper>
        </Box>
      </Box>
      <PeripheryDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        mode="local"
        controllerId={selectedId}
        existingDevices={devices}
        onAdd={(device, extras) => {
          const wizard = catalogWizardFor(device.type);
          if (wizard) {
            if (!schema) {
              return;
            }
            const next = ensureGraph(wizard.apply(values as Record<string, unknown>, device));
            setValues(next as ConfigValues);
            setContent(generateConfigJson(schema, next as ConfigValues, content));
          } else {
            writeDevices([...devices, device], extras?.routes);
          }
          setInstance(device.id);
        }}
        onFlowCommitted={({ name }) => {
          setStatus(t('deviceConfig.wizard.flowDone', { name }));
        }}
        controllerHostname={controller?.hostname || controller?.id || selectedId}
      />
    </Container>
  );
};

export default DeviceConfigEditor;
